// pages/TestQuizRunner.jsx
// Web-based test runner interface for browser tests
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../Menu';

const isLocalhost = () => {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname === '::1';
};

export default function TestQuizRunner() {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);
  const [results, setResults] = useState(null);
  const [config, setConfig] = useState({
    numTestUsers: 1,
    gameUrl: 'General_Trivia_Live_rob_1762136320971',
    joinCode: '3001',
    runGameTests: true,
    runFormTests: true,
    runWorkflowTests: true,
    runResponsiveTests: false,
    runErrorTests: false,
    runConsoleTests: true,
    headless: false,
    recordVideo: true
  });

  // Security check - only allow on localhost
  useEffect(() => {
    if (!isLocalhost()) {
      navigate('/host');
    }
  }, [navigate]);

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const startTests = async () => {
    if (running) return;
    
    setRunning(true);
    setLogs([]);
    setResults(null);

    try {
      // Call API endpoint to start tests (via setupProxy.js)
      const response = await fetch('/api/test-runner/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'log') {
                setLogs(prev => [...prev, { message: data.message, timestamp: new Date() }]);
              } else if (data.type === 'complete') {
                setResults(data.results);
                setRunning(false);
              } else if (data.type === 'error') {
                setLogs(prev => [...prev, { message: `ERROR: ${data.message}`, timestamp: new Date(), error: true }]);
                setRunning(false);
              }
            } catch (e) {
              // Ignore JSON parse errors for non-JSON lines
            }
          }
        }
      }
    } catch (error) {
      setLogs(prev => [...prev, { 
        message: `Failed to start tests: ${error.message}`, 
        timestamp: new Date(), 
        error: true 
      }]);
      setRunning(false);
    }
  };

  const stopTests = async () => {
    try {
      await fetch('/api/test-runner/stop', { method: 'POST' });
      setRunning(false);
    } catch (error) {
      console.error('Failed to stop tests:', error);
    }
  };

  if (!isLocalhost()) {
    return (
      <div style={{ padding: 20, color: '#fff' }}>
        <h2>Test Runner</h2>
        <p>This page is only available on localhost.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: '#223b4c',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
      }}
    >
      {/* Top Bar */}
      <div
        style={{
          width: '100vw',
          padding: '14px 16px',
          background: 'rgba(20,15,44,0.55)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          position: 'sticky',
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: '#e2eefd',
            fontWeight: 900,
            borderRadius: 12,
            padding: '10px 14px',
            cursor: 'pointer',
          }}
        >
          ☰ Menu
        </button>
        <div style={{ marginLeft: 8, fontWeight: 900, color: '#f8faff' }}>
          Test Runner
        </div>
      </div>

      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          padding: '24px',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          color: '#fff',
        }}
      >
        <h1 style={{ color: '#e2eefd', marginBottom: 24 }}>
          Browser Test Runner
        </h1>

        {/* Configuration Panel */}
        <div
          style={{
            background: 'rgba(28,34,58,0.67)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: 24,
            boxShadow: '0 8px 36px 8px #111b',
          }}
        >
          <h2 style={{ color: '#ffe066', marginBottom: 20 }}>Test Configuration</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
            {/* Number of Test Users */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 700 }}>
                Number of Test Users
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={config.numTestUsers}
                onChange={(e) => handleConfigChange('numTestUsers', parseInt(e.target.value) || 1)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #8cd4ff',
                  background: 'rgba(40,65,110,0.13)',
                  color: '#e8f4ff',
                  fontSize: '1em',
                }}
                disabled={running}
              />
            </div>

            {/* Game URL */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 700 }}>
                Game Code/URL
              </label>
              <input
                type="text"
                value={config.gameUrl}
                onChange={(e) => handleConfigChange('gameUrl', e.target.value)}
                placeholder="General_Trivia_Live_rob_1762136320971"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #8cd4ff',
                  background: 'rgba(40,65,110,0.13)',
                  color: '#e8f4ff',
                  fontSize: '1em',
                }}
                disabled={running}
              />
            </div>

            {/* Join Code */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 700 }}>
                Join Code
              </label>
              <input
                type="text"
                value={config.joinCode}
                onChange={(e) => handleConfigChange('joinCode', e.target.value)}
                placeholder="3001"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #8cd4ff',
                  background: 'rgba(40,65,110,0.13)',
                  color: '#e8f4ff',
                  fontSize: '1em',
                }}
                disabled={running}
              />
            </div>
          </div>

          {/* Test Suite Selection */}
          <div style={{ marginTop: 24 }}>
            <label style={{ display: 'block', marginBottom: 12, fontWeight: 700 }}>
              Test Suites
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              {[
                { key: 'runGameTests', label: 'Game Tests' },
                { key: 'runFormTests', label: 'Form Tests' },
                { key: 'runWorkflowTests', label: 'Workflow Tests' },
                { key: 'runResponsiveTests', label: 'Responsive Tests' },
                { key: 'runErrorTests', label: 'Error Tests' },
                { key: 'runConsoleTests', label: 'Console Tests' },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: running ? 'not-allowed' : 'pointer',
                    opacity: running ? 0.6 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={config[key]}
                    onChange={(e) => handleConfigChange(key, e.target.checked)}
                    disabled={running}
                    style={{ width: 20, height: 20 }}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Options */}
          <div style={{ marginTop: 24 }}>
            <label style={{ display: 'block', marginBottom: 12, fontWeight: 700 }}>
              Options
            </label>
            <div style={{ display: 'flex', gap: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.6 : 1 }}>
                <input
                  type="checkbox"
                  checked={config.headless}
                  onChange={(e) => handleConfigChange('headless', e.target.checked)}
                  disabled={running}
                  style={{ width: 20, height: 20 }}
                />
                <span>Headless Mode</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.6 : 1 }}>
                <input
                  type="checkbox"
                  checked={config.recordVideo}
                  onChange={(e) => handleConfigChange('recordVideo', e.target.checked)}
                  disabled={running}
                  style={{ width: 20, height: 20 }}
                />
                <span>Record Video</span>
              </label>
            </div>
          </div>

          {/* Control Buttons */}
          <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
            <button
              onClick={startTests}
              disabled={running}
              style={{
                background: running ? '#666' : 'linear-gradient(135deg, #ffe066 0%, #47e373 100%)',
                color: '#273b44',
                fontWeight: 900,
                borderRadius: '12px',
                padding: '12px 24px',
                border: 'none',
                fontSize: '1.1em',
                cursor: running ? 'not-allowed' : 'pointer',
                opacity: running ? 0.6 : 1,
              }}
            >
              {running ? 'Running...' : 'Start Tests'}
            </button>
            {running && (
              <button
                onClick={stopTests}
                style={{
                  background: '#ff4a4a',
                  color: '#fff',
                  fontWeight: 700,
                  borderRadius: '12px',
                  padding: '12px 24px',
                  border: 'none',
                  fontSize: '1.1em',
                  cursor: 'pointer',
                }}
              >
                Stop Tests
              </button>
            )}
          </div>
        </div>

        {/* Results Panel */}
        {results && (
          <div
            style={{
              background: 'rgba(28,34,58,0.67)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: 24,
              boxShadow: '0 8px 36px 8px #111b',
            }}
          >
            <h2 style={{ color: '#ffe066', marginBottom: 20 }}>Test Results</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2em', fontWeight: 900, color: '#47e373' }}>
                  {results.passed || 0}
                </div>
                <div style={{ color: '#aaa' }}>Passed</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2em', fontWeight: 900, color: '#ff4a4a' }}>
                  {results.failed || 0}
                </div>
                <div style={{ color: '#aaa' }}>Failed</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2em', fontWeight: 900, color: '#8cd4ff' }}>
                  {results.total || 0}
                </div>
                <div style={{ color: '#aaa' }}>Total</div>
              </div>
            </div>
            {results.outputFolder && (
              <div style={{ padding: '12px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                <strong>Output Folder:</strong> {results.outputFolder}
              </div>
            )}
          </div>
        )}

        {/* Logs Panel */}
        <div
          style={{
            background: 'rgba(28,34,58,0.67)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 36px 8px #111b',
          }}
        >
          <h2 style={{ color: '#ffe066', marginBottom: 20 }}>Test Logs</h2>
          <div
            style={{
              background: 'rgba(0,0,0,0.4)',
              borderRadius: '8px',
              padding: '16px',
              maxHeight: '500px',
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.9em',
            }}
          >
            {logs.length === 0 ? (
              <div style={{ color: '#aaa' }}>No logs yet. Start a test to see output.</div>
            ) : (
              logs.map((log, idx) => (
                <div
                  key={idx}
                  style={{
                    color: log.error ? '#ffaaaa' : '#e8f4ff',
                    marginBottom: '4px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  [{log.timestamp.toLocaleTimeString()}] {log.message}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
